import classNames from 'classnames';
import { CellAction, useCellActionsExecutor } from 'context/CellActions';
import { useDraftjsCallToActionEditor } from 'modules/draftjs/hooks/useDraftjsCallToActionEditor';
import { useLexicalCallToActionEditor } from 'modules/Lexical/hooks/useLexicalCallToActionEditor';
import { useLexicalState } from 'modules/Lexical/hooks/useLexicalState';
import { useLexicalStateCache } from 'modules/Lexical/hooks/useLexicalStateCache';
import { useLexicalUndo } from 'modules/Lexical/hooks/useLexicalUndo';
import React from 'react';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import Toolbar from 'components/Toolbar/CallToAction';
import UndoPortal from 'components/UndoRedo/UndoPortal';
import * as Constants from 'const';
import { useEditorToggle } from 'containers/EditorToggle/useEditorToggle';
import { getActiveLayer } from 'hooks/useActiveLayer';
import useBrandProps from 'hooks/useBrandProps';
import { useProjectType } from 'hooks/useProjectType';
import { AssetAlignment } from 'models';
import { allowLinkRedirect } from 'utils/allowLinkRedirect';
import * as styleUtils from 'utils/styles';
import { useHistoryGlobalState } from '../../../hooks/useHistoryGlobalState';
import editorWrapperCss from '../editor-wrapper.module.scss';
import DraftjsButton from './components/DraftjsButton';
import LexicalButton from './components/LexicalButton';
import ResizableButton from './components/ResizableButton';
import { DraftjsCTAEditorProvider } from './context/DraftjsCTAEditor';
import { LexicalCTAEditorProvider } from './context/LexicalCTAEditor';
import { ResizeProvider } from './context/Resize';
import { EditorHook } from './editor';
import { useCallToAction } from './hooks';
import { useHistoryExtraState } from './hooks/useHistoryExtraState';
import { useLink } from './hooks/useLink';
import { useResizeObserverCTA } from './hooks/useResizeObserverCTA';
import { useSave } from './hooks/useSave';
import { useSizeReset } from './hooks/useSizeReset';
import { useStyles } from './hooks/useStyles';
import { useUndo } from './hooks/useUndo';
import { CallToActionProps } from './models';
import css from './styles.module.scss';
import { wrapEditorSettersWithUndo, wrapUndoMiddleware } from './utils/draftjs-undo';

export function getCallToActionDOMId(relationId: string): string {
  return `call-to-action-cell-${relationId}`;
}

const CallToAction: React.FunctionComponent<CallToActionProps> = (props) => { //NOSONAR
  const {
    cellHeight,
    cellWidth,
    disableCellWidthEditing,
    document,
    editMode,
    isIncreasingWidthDisabled,
    layoutId,
    maxCellHeight,
    minCellHeight,
    relation,
    toggleColumnWidth,
    toggleRowHeight,
    toggleAutoFitContent,
    isAutoFitContent,
    images,
    isDraggingAsset,
  } = props;

  const projectType = useProjectType();
  const activeLayer = getActiveLayer();
  const relationId = relation.get('id');
  const brandProps = useBrandProps(relationId);

  const editorWrap = React.useRef<HTMLDivElement>(null);

  const stylesHook = useStyles(
    relation,
    activeLayer,
    { colors: brandProps.colors, images, defaultAssetBackgroundColor: Constants.DefaultCallToActionBackgroundColor },
    Constants.ProjectsConfig[projectType],
    cellWidth,
  );

  const { styles, css: stylesCSS, getMaxWidth } = stylesHook;

  const sizeResetHook = useSizeReset(props, stylesHook);

  const { isLexicalMode, isBothMode, isDraftjsMode } = useEditorToggle();
  const lexicalHook = useLexicalCallToActionEditor(sizeResetHook, editorWrap);
  const draftjsHook = useDraftjsCallToActionEditor(props, brandProps, sizeResetHook, editorWrap);
  const editorHook: EditorHook = isLexicalMode ? lexicalHook : draftjsHook;

  const lexicalState = useLexicalState({
    draftjsContent: draftjsHook.editorState.getCurrentContent(),
    colors: brandProps.colors,
    fonts: brandProps.fonts,
    lexicalState: document?.get('lexicalState'),
  });
  const cachedLexicalState = useLexicalStateCache(lexicalHook.editorRef, editMode, lexicalState);

  const { link, toggleLink } = useLink(document);

  const {
    container,
    modifiedStylesSetters: stylesSetters,
    correctedHeight,
    correctedWidth,
    getResizeComponentHeight,
    getResizeComponentMinHeight,
    getResizeComponentWidth,
    finishResizing,
    onResize,
  } = useCallToAction(props, stylesHook, sizeResetHook, editorWrap);

  useSave(props, stylesHook, sizeResetHook, editorHook, link);
  useResizeObserverCTA(container, editorWrap, styles, stylesSetters, editMode, isDraggingAsset);

  const {
    setCursorOnAbbreviation,
    applySelection,
    returnFocusToEditor,
  } = editorHook;

  const history = useHistoryExtraState(
    props,
    stylesHook,
    { link, toggleLink, height: correctedHeight, width: correctedWidth },
  );
  const lexicalUndo = useLexicalUndo(lexicalHook.editorRef);
  const draftjsUndo = useUndo(
    editMode,
    draftjsHook,
    stylesHook,
    { toggleAutoFitContent, toggleLink, toggleRowHeight, toggleColumnWidth },
    history.extraState,
  );
  const undoHook = isLexicalMode ? lexicalUndo : draftjsUndo;
  useHistoryGlobalState(editMode, undoHook.isUndoDisabled);

  const handleClick = (event: React.MouseEvent): void => {
    if (allowLinkRedirect(event) && link) {
      window.open(link, '_blank');
    }
  };

  useCellActionsExecutor(CellAction.SET_CURSOR_ON_ABBREVIATION, setCursorOnAbbreviation);
  useCellActionsExecutor(CellAction.APPLY_SELECTION, applySelection);

  const buttonStyles = {
    ...stylesCSS.element,
    ...styleUtils.getButtonSize(correctedHeight, correctedWidth, editMode),
  };

  const id = getCallToActionDOMId(relationId);
  const DraftjsButtonProps = {
    id,
    editorState: draftjsHook.editorState,
    editorRef: draftjsHook.editorRef,
    cssButton: css.button,
    styles: buttonStyles,
    editMode,
    editorWrap,
    onEditorChange: draftjsHook.onEditorChange,
    returnFocusToEditor,
    undoHook: draftjsUndo,
    brandProps,
  };

  const LexicalButtonProps = {
    id,
    editorState: cachedLexicalState,
    editorRef: lexicalHook.editorRef,
    onChange: lexicalHook.onChange,
    onStopEditing: lexicalHook.onStopEditing,
    cssButton:css.button,
    styles: buttonStyles,
    editMode,
    editorWrap,
    returnFocusToEditor,
    brandProps,
    history,
    historyOnChange: lexicalUndo.onChange,
  };

  const resizeProps = {
    alignment: styles.alignment.toJS() as AssetAlignment,
    container: container.current ?? undefined,
    startResizing: isLexicalMode ? undefined : (currentWidth: number, currentHeight: number): void => {
      draftjsUndo.undoStackMiddleware(() => {}, 'width', true)(currentWidth);
      draftjsUndo.undoStackMiddleware(() => {}, 'height', true)(currentHeight);
    },
    finishResizing,
    onResize,
    width: getResizeComponentWidth(),
    height: getResizeComponentHeight(),
    minHeight: getResizeComponentMinHeight(),
  };

  return (
    <AccessibleDiv
      className={css.CallToAction}
      onClick={handleClick}
      ref={container}
      style={stylesCSS.container}
    >
      <DraftjsCTAEditorProvider value={DraftjsButtonProps}>
        <LexicalCTAEditorProvider value={LexicalButtonProps}>
          <ResizeProvider value={resizeProps}>
            <div className={classNames({
              [editorWrapperCss.notBothMode]: !isBothMode,
              [editorWrapperCss.editorModeBothWrapper]: isBothMode,
              [editorWrapperCss.editorModeLexicalWrapper]: isLexicalMode,
            })}>
              {(isDraftjsMode || isBothMode) && (
                editMode
                  ? <ResizableButton><DraftjsButton/></ResizableButton>
                  : <DraftjsButton/>

              )}
              {(isLexicalMode || isBothMode) && (
                (editMode && isLexicalMode)
                  ? <ResizableButton><LexicalButton/></ResizableButton>
                  : <LexicalButton/>
              )}
            </div>
          </ResizeProvider>
        </LexicalCTAEditorProvider>
      </DraftjsCTAEditorProvider>
      {
        editMode &&
          <>
            <UndoPortal
              undo={undoHook.undo}
              redo={undoHook.redo}
              isRedoDisabled={undoHook.isRedoDisabled}
              isUndoDisabled={undoHook.isUndoDisabled}
            />
            <Toolbar
              editorProps={editorHook.props}
              editorSetters={
                isLexicalMode
                  ? lexicalHook.setters
                  : wrapEditorSettersWithUndo(draftjsUndo.undoStackMiddleware, draftjsHook.setters)
              }
              styles={styles}
              stylesSetters={
                isLexicalMode
                  ? stylesSetters
                  : wrapUndoMiddleware(draftjsUndo.undoStackMiddleware, stylesSetters)
              }
              projectType={projectType}
              returnFocusToEditor={returnFocusToEditor}
              cellHeight={cellHeight}
              maxCellHeight={maxCellHeight}
              minCellHeight={minCellHeight}
              toggleCellHeight={
                isLexicalMode
                  ? toggleRowHeight
                  : draftjsUndo.undoStackMiddleware(toggleRowHeight, 'cellHeight') as (newHeight: number) => number
              }
              cellWidth={cellWidth}
              toggleColumnWidth={
                isLexicalMode
                  ? toggleColumnWidth
                  : draftjsUndo.undoStackMiddleware(toggleColumnWidth, 'cellWidth') as (newWidth: number) => number
              }
              link={link}
              toggleLink={
                isLexicalMode
                  ? toggleLink
                  : draftjsUndo.undoStackMiddleware(toggleLink, 'link')
              }
              layoutId={layoutId}
              disableCellWidthEditing={disableCellWidthEditing}
              height={getResizeComponentHeight()}
              width={getResizeComponentWidth()}
              maxWidth={getMaxWidth()}
              isIncreasingWidthDisabled={isIncreasingWidthDisabled}
              toggleAutoFitContent={
                isLexicalMode
                  ? toggleAutoFitContent
                  : draftjsUndo.undoStackMiddleware(toggleAutoFitContent, 'isAutoFitContent')
              }
              isAutoFitContent={isAutoFitContent}
            />
          </>
      }
    </AccessibleDiv>
  );
};

export default CallToAction;
